import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";

const initialState = {
  // Finance Events
  financeEvents: [] as any[],
  financeEventsLoading: false,
  financeEventsError: null as any,
  
  // Finance Overview (4 cards)
  financeOverview: {
    netPosition: 0,
    moneyIn: 0,
    moneyOut: 0,
    currentMonthExpenses: 0
  } as any,
  financeOverviewLoading: false,
  financeOverviewError: null as any,
  
  // Daily Ledger
  dailyLedger: {} as any,
  dailyLedgerLoading: false,
  dailyLedgerError: null as any,
  
  // Recent Activity
  recentActivity: [] as any[],
  recentActivityLoading: false,
  recentActivityError: null as any,
  
  // Financial Health Score
  healthScore: null as any,
  healthScoreLoading: false,
  healthScoreError: null as any,
  
  // Financial Intelligence
  financialIntelligence: null as any,
  financialIntelligenceLoading: false,
  financialIntelligenceError: null as any,
  
  // Expenses
  expenses: [] as any[],
  expenseCategories: [] as any[],
  expensesLoading: false,
  expensesError: null as any,
  
  // General
  isLoading: false,
  isError: false,
  error: null as any,
};

// Finance Events Actions
export const getFinanceEvents = createAsyncThunk(
  "finances/getFinanceEvents",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, dateRange, eventType, page = 1, limit = 50 } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/finance/events`, {
        dateRange,
        eventType,
        page,
        limit
      });
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPayment = createAsyncThunk(
  "finances/createPayment", 
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...paymentData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/finance/payments/create`, paymentData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createOwnerTransaction = createAsyncThunk(
  "finances/createOwnerTransaction",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, type, ...transactionData } = params;
      
      const endpoint = type === 'deposit' ? '/finance/owner/deposit' : '/finance/owner/withdrawal';
      const response = await axiosApi({ token, organizationId }).post(endpoint, transactionData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Expenses Actions
export const getExpenses = createAsyncThunk(
  "finances/getExpenses",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...filterParams } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/expenses`, filterParams);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createExpense = createAsyncThunk(
  "finances/createExpense",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...expenseData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/expenses/create`, expenseData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateExpense = createAsyncThunk(
  "finances/updateExpense", 
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...expenseData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/expenses/update`, expenseData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const approveExpenses = createAsyncThunk(
  "finances/approveExpenses",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...approvalData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/expenses/approve`, approvalData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectExpenses = createAsyncThunk(
  "finances/rejectExpenses",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...rejectionData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/expenses/reject`, rejectionData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getExpenseCategories = createAsyncThunk(
  "finances/getExpenseCategories",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId } = params;
      
      const response = await axiosApi({ token, organizationId }).get(`/expenses/categories/list`);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const getFinanceOverview = createAsyncThunk(
  "finances/getFinanceOverview",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, dateRange } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const response = await axiosApi({ token, organizationId }).post(`/finance/overview`, {
        dateRange
      });
      
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getDailyLedger = createAsyncThunk(
  "finances/getDailyLedger",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, dateRange, aggregation } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const response = await axiosApi({ token, organizationId }).post(`/finance/ledger/daily`, {
        dateRange,
        aggregation: aggregation || 'daily'
      });
      
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getRecentActivity = createAsyncThunk(
  "finances/getRecentActivity",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, limit } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const response = await axiosApi({ token, organizationId }).get(`/finance/activity/recent?${limit ? `limit=${limit}` : ''}`);
      
      return response.data.data.activities || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFinancialHealthScore = createAsyncThunk(
  "finances/getFinancialHealthScore",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, dateRange } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const response = await axiosApi({ token, organizationId }).post(`/finance/health-score`, {
        dateRange
      });
      
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFinancialIntelligence = createAsyncThunk(
  "finances/getFinancialIntelligence",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, dateRange } = params;
      
      if (!organizationId) {
        return rejectWithValue("Organization ID is required");
      }

      const response = await axiosApi({ token, organizationId }).post(`/finance/intelligence`, {
        dateRange
      });
      
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const financesSlice = createSlice({
  name: "finances",
  initialState,
  reducers: {
    clearFinanceErrors: (state) => {
      state.financeEventsError = null;
      state.expensesError = null;
      state.error = null;
      state.isError = false;
    },
    resetFinanceState: (state) => {
      return initialState;
    },
    updateFinanceEventLocally: (state, action) => {
      const { eventId, updates } = action.payload;
      const eventIndex = state.financeEvents.findIndex((event: any) => event.id === eventId);
      if (eventIndex !== -1) {
        state.financeEvents[eventIndex] = { ...state.financeEvents[eventIndex], ...updates };
      }
    },
    updateExpenseLocally: (state, action) => {
      const { expenseId, updates } = action.payload;
      const expenseIndex = state.expenses.findIndex((expense: any) => expense.id === expenseId);
      if (expenseIndex !== -1) {
        state.expenses[expenseIndex] = { ...state.expenses[expenseIndex], ...updates };
      }
    }
  },
  extraReducers: (builder) => {
    // Finance Events
    builder
      .addCase(getFinanceEvents.pending, (state) => {
        state.financeEventsLoading = true;
        state.financeEventsError = null;
      })
      .addCase(getFinanceEvents.fulfilled, (state, action) => {
        state.financeEventsLoading = false;
        state.financeEvents = action.payload?.data?.events || [];
      })
      .addCase(getFinanceEvents.rejected, (state, action) => {
        state.financeEventsLoading = false;
        state.financeEventsError = action.payload;
      });

    // Create Payment
    builder
      .addCase(createPayment.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add new payment event to the list
        if (action.payload?.data) {
          state.financeEvents.unshift(action.payload.data);
        }
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      });

    // Create Owner Transaction
    builder
      .addCase(createOwnerTransaction.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOwnerTransaction.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add new transaction event to the list
        if (action.payload?.data) {
          state.financeEvents.unshift(action.payload.data);
        }
      })
      .addCase(createOwnerTransaction.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      });

    // Expenses
    builder
      .addCase(getExpenses.pending, (state) => {
        state.expensesLoading = true;
        state.expensesError = null;
      })
      .addCase(getExpenses.fulfilled, (state, action) => {
        state.expensesLoading = false;
        state.expenses = action.payload?.data?.expenses || [];
      })
      .addCase(getExpenses.rejected, (state, action) => {
        state.expensesLoading = false;
        state.expensesError = action.payload;
      });

    builder
      .addCase(createExpense.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.expenses.unshift(action.payload.data);
        }
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        if (action.payload?.data) {
          const updatedExpense = action.payload.data;
          const index = state.expenses.findIndex((exp: any) => exp.id === updatedExpense.id);
          if (index !== -1) {
            state.expenses[index] = updatedExpense;
          }
        }
      })
      .addCase(approveExpenses.fulfilled, (state, action) => {
        // Update approved expenses in the list
        if (action.payload?.data?.updatedExpenses) {
          action.payload.data.updatedExpenses.forEach((updatedExpense: any) => {
            const index = state.expenses.findIndex((exp: any) => exp.id === updatedExpense.id);
            if (index !== -1) {
              state.expenses[index] = updatedExpense;
            }
          });
        }
      })
      .addCase(rejectExpenses.fulfilled, (state, action) => {
        // Update rejected expenses in the list
        if (action.payload?.data?.updatedExpenses) {
          action.payload.data.updatedExpenses.forEach((updatedExpense: any) => {
            const index = state.expenses.findIndex((exp: any) => exp.id === updatedExpense.id);
            if (index !== -1) {
              state.expenses[index] = updatedExpense;
            }
          });
        }
      });

    // Expense Categories
    builder
      .addCase(getExpenseCategories.fulfilled, (state, action) => {
        state.expenseCategories = action.payload?.data || [];
      });


    // Finance Overview
    builder
      .addCase(getFinanceOverview.pending, (state) => {
        state.financeOverviewLoading = true;
        state.financeOverviewError = null;
      })
      .addCase(getFinanceOverview.fulfilled, (state, action) => {
        state.financeOverviewLoading = false;
        state.financeOverview = action.payload;
      })
      .addCase(getFinanceOverview.rejected, (state, action) => {
        state.financeOverviewLoading = false;
        state.financeOverviewError = action.payload;
      });

    // Daily Ledger
    builder
      .addCase(getDailyLedger.pending, (state) => {
        state.dailyLedgerLoading = true;
        state.dailyLedgerError = null;
      })
      .addCase(getDailyLedger.fulfilled, (state, action) => {
        state.dailyLedgerLoading = false;
        state.dailyLedger = action.payload;
      })
      .addCase(getDailyLedger.rejected, (state, action) => {
        state.dailyLedgerLoading = false;
        state.dailyLedgerError = action.payload;
      });

    // Recent Activity
    builder
      .addCase(getRecentActivity.pending, (state) => {
        state.recentActivityLoading = true;
        state.recentActivityError = null;
      })
      .addCase(getRecentActivity.fulfilled, (state, action) => {
        state.recentActivityLoading = false;
        state.recentActivity = action.payload;
      })
      .addCase(getRecentActivity.rejected, (state, action) => {
        state.recentActivityLoading = false;
        state.recentActivityError = action.payload;
      });

    // Financial Health Score
    builder
      .addCase(getFinancialHealthScore.pending, (state) => {
        state.healthScoreLoading = true;
        state.healthScoreError = null;
      })
      .addCase(getFinancialHealthScore.fulfilled, (state, action) => {
        state.healthScoreLoading = false;
        state.healthScore = action.payload;
      })
      .addCase(getFinancialHealthScore.rejected, (state, action) => {
        state.healthScoreLoading = false;
        state.healthScoreError = action.payload;
      });

    // Financial Intelligence
    builder
      .addCase(getFinancialIntelligence.pending, (state) => {
        state.financialIntelligenceLoading = true;
        state.financialIntelligenceError = null;
      })
      .addCase(getFinancialIntelligence.fulfilled, (state, action) => {
        state.financialIntelligenceLoading = false;
        state.financialIntelligence = action.payload;
      })
      .addCase(getFinancialIntelligence.rejected, (state, action) => {
        state.financialIntelligenceLoading = false;
        state.financialIntelligenceError = action.payload;
      });
  },
});

export const {
  clearFinanceErrors,
  resetFinanceState,
  updateFinanceEventLocally,
  updateExpenseLocally
} = financesSlice.actions;


export default financesSlice.reducer;